/**
 * Dev-only performance and logging utilities for Talousakatemia
 * 
 * Use these for ad-hoc UI/component timing measurements during development.
 * For database query and startup metrics, use performanceMonitor.ts instead.
 * 
 * All utilities are tree-shaken out of production builds.
 */

/**
 * Log a performance measurement in development mode only.
 * 
 * @param label - Description of what's being measured
 * @param startTime - Optional start time from performance.now()
 * 
 * @example
 * // Log a timestamp
 * devPerfLog("Free World – component mounted");
 * 
 * @example
 * // Measure duration
 * const start = performance.now();
 * await fetchLocations();
 * devPerfLog("Free World – initial data load", start);
 */
export function devPerfLog(label: string, startTime?: number): void {
  // Only log in development mode (Vite's import.meta.env.DEV)
  if (!import.meta.env.DEV) return;
  
  // Check if performance API is available
  if (typeof performance === "undefined") return;

  if (startTime != null) {
    const delta = performance.now() - startTime;
    // eslint-disable-next-line no-console
    console.log(`[PERF] Talousakatemia – ${label}: ${delta.toFixed(1)} ms`);
  } else {
    // eslint-disable-next-line no-console
    console.log(
      `[PERF] Talousakatemia – ${label} at ${performance.now().toFixed(1)} ms`
    );
  }
}

/**
 * Create a performance timer that can be ended later.
 * Useful for measuring async operations.
 * 
 * @param label - Description of what's being measured
 * @returns Function to call when the operation is complete
 * 
 * @example
 * const endTimer = startPerfTimer("Module – data fetch");
 * await fetchModuleData();
 * endTimer();
 */
export function startPerfTimer(label: string): () => void {
  if (!import.meta.env.DEV) return () => {};
  
  const start = performance.now();
  return () => devPerfLog(label, start);
}

/**
 * Log a categorized message in development mode only.
 * Replaces scattered console.log statements with a consistent format.
 * 
 * @param category - Short category name (e.g., "FWBalance", "TimeProgress", "Wallet")
 * @param message - The message to log
 * @param data - Optional data to include
 * 
 * @example
 * devLog("FWBalance", "Backend computed balance", { balance: 995 });
 */
// Debug flag - disabled for production
const FORCE_DEBUG_LOGS = false;

export function devLog(category: string, message: string, data?: unknown): void {
  if (!FORCE_DEBUG_LOGS && !import.meta.env.DEV) return;
  
  if (data !== undefined) {
    // eslint-disable-next-line no-console
    console.info(`[${category}] ${message}`, data);
  } else {
    // eslint-disable-next-line no-console
    console.info(`[${category}] ${message}`);
  }
}

/**
 * Log a warning in development mode only.
 * 
 * @param category - Short category name
 * @param message - The warning message
 * @param data - Optional data to include
 */
export function devWarn(category: string, message: string, data?: unknown): void {
  if (!import.meta.env.DEV) return;
  
  if (data !== undefined) {
    // eslint-disable-next-line no-console
    console.warn(`[${category}] ⚠️ ${message}`, data);
  } else {
    // eslint-disable-next-line no-console
    console.warn(`[${category}] ⚠️ ${message}`);
  }
}

/**
 * Wrap an async function with performance timing.
 * Returns the result of the function and logs the duration.
 * 
 * @param label - Description of the operation
 * @param fn - Async function to execute
 * @returns The result of the function
 * 
 * @example
 * const balance = await withPerfTiming("FWBalance – RPC call", async () => {
 *   return await supabase.rpc('get_freeworld_balance', { p_player_id: playerId });
 * });
 */
export async function withPerfTiming<T>(
  label: string,
  fn: () => Promise<T>
): Promise<T> {
  if (!import.meta.env.DEV) return fn();
  
  const start = performance.now();
  try {
    return await fn();
  } finally {
    devPerfLog(label, start);
  }
}
